import { dataAnalyticsExercises } from "~/features/computer/data/da/da.exercises";
import { digitalLiteracyExercises } from "~/features/computer/data/digital-literacy/digital-literacy.exercises";
import { dsExercises } from "~/features/computer/data/ds/ds.exercises";
import { emergingTechExercises } from "~/features/computer/data/emerging/emerging.exercises";
import { entrepreneurshipExercises } from "~/features/computer/data/entrepreneurship/entrepreneurship.exercises";
import { algorithmExercises } from "~/features/computer/data/exercises/algorithm.exercises";
import { expressionsExercises } from "~/features/computer/data/exercises/expressions.exercises";
import { loopsExercises } from "~/features/computer/data/exercises/loops.exercises";
import { operatorsExercises } from "~/features/computer/data/exercises/operators.exercises";
import { verifiedTopicExercises } from "~/features/computer/data/exercises/mcq.units";
import { variablesExercises } from "~/features/computer/data/exercises/variables.exercises";
import { legalEthicsExercises } from "~/features/computer/data/legal-ethics/legal-ethics.exercises";
import { researchedMcqAdditionsByUnit, researchedPythonMcqAdditionsByGroup } from "~/features/computer/data/mcq-research-additions";
import { sdlcExercises } from "~/features/computer/data/sdlc/sdlc.exercises";
import type { ComputerMcq, ComputerMcqChapter, ComputerMcqGroup, ComputerMcqUnit } from "~/features/computer/types/computer";

type ChapterSeed = Omit<ComputerMcqChapter, "questions" | "groups"> & {
  questions?: ComputerMcq[];
  groups?: ComputerMcqGroup[];
};

function withMeta(
  questions: ComputerMcq[],
  meta: Pick<ComputerMcq, "unit" | "topic" | "topicLabel" | "conceptTo" | "chapterTo">,
): ComputerMcq[] {
  return questions.map((question) => ({
    ...question,
    ...meta,
  }));
}

function normalizeQuestion(question: string) {
  return question
    .toLowerCase()
    .replace(/[`'".,!?():/\\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function mergeUniqueQuestions(...groups: ComputerMcq[][]) {
  const seen = new Set<string>();
  const merged: ComputerMcq[] = [];

  groups.flat().forEach((question) => {
    const key = normalizeQuestion(question.question);

    if (!seen.has(key)) {
      seen.add(key);
      merged.push(question);
    }
  });

  return merged;
}

const pythonTopicGroups = [
  {
    key: "variables",
    label: "Variables",
    description: "Variables aur named storage ke foundational MCQs.",
    conceptTo: "/11th/computer/python/variables/",
    questions: mergeUniqueQuestions(
      withMeta(variablesExercises.mcqs, {
        unit: "python",
        topic: "variables",
        topicLabel: "Variables",
        conceptTo: "/11th/computer/python/variables/",
        chapterTo: "/11th/computer/python/",
      }),
      withMeta(researchedPythonMcqAdditionsByGroup.variables ?? [], {
        unit: "python",
        topic: "variables",
        topicLabel: "Variables",
        conceptTo: "/11th/computer/python/variables/",
        chapterTo: "/11th/computer/python/",
      }),
    ),
  },
  {
    key: "input-output",
    label: "Input / Output",
    description: "input() aur print() ke basic objective questions.",
    conceptTo: "/11th/computer/python/programs/",
    questions: mergeUniqueQuestions(
      withMeta(
        [
          {
            id: "io1",
            difficulty: "easy",
            question: "Python me user se value lene ke liye konsa function use hota hai?",
            options: ["print()", "input()", "len()", "range()"],
            correctIndex: 1,
            explanation: "input() user se value leta hai. print() output dikhata hai, value leta nahi.",
          },
          {
            id: "io2",
            difficulty: "easy",
            question: "Output screen par dikhane ke liye konsa function use hota hai?",
            options: ["print()", "input()", "append()", "type()"],
            correctIndex: 0,
            explanation: "print() result ya message screen par show karta hai.",
          },
        ],
        {
          unit: "python",
          topic: "input-output",
          topicLabel: "Input / Output",
          conceptTo: "/11th/computer/python/programs/",
          chapterTo: "/11th/computer/python/",
        },
      ),
      withMeta(researchedPythonMcqAdditionsByGroup["input-output"] ?? [], {
        unit: "python",
        topic: "input-output",
        topicLabel: "Input / Output",
        conceptTo: "/11th/computer/python/programs/",
        chapterTo: "/11th/computer/python/",
      }),
    ),
  },
  {
    key: "operators",
    label: "Operators",
    description: "Operators aur expressions ke objective questions.",
    conceptTo: "/11th/computer/python/operators/",
    questions: mergeUniqueQuestions(
      withMeta([...operatorsExercises.mcqs, ...expressionsExercises.mcqs], {
        unit: "python",
        topic: "operators",
        topicLabel: "Operators",
        conceptTo: "/11th/computer/python/operators/",
        chapterTo: "/11th/computer/python/",
      }),
      withMeta(researchedPythonMcqAdditionsByGroup.operators ?? [], {
        unit: "python",
        topic: "operators",
        topicLabel: "Operators",
        conceptTo: "/11th/computer/python/operators/",
        chapterTo: "/11th/computer/python/",
      }),
    ),
  },
  {
    key: "if-else",
    label: "If-Else",
    description: "Decision making aur conditional logic MCQs.",
    conceptTo: "/11th/computer/python/programs/",
    questions: mergeUniqueQuestions(
      withMeta(
        [
          {
            id: "if1",
            difficulty: "easy",
            question: "If-else ka main purpose kya hai?",
            options: ["Loop chalana", "Decision lena", "List banana", "Comment likhna"],
            correctIndex: 1,
            explanation: "If-else condition check karke decision leta hai.",
          },
          {
            id: "if2",
            difficulty: "easy",
            question: "Agar condition false ho to konsa block chalega?",
            options: ["if block", "else block", "print block", "range block"],
            correctIndex: 1,
            explanation: "Condition false hone par else block run hota hai.",
          },
        ],
        {
          unit: "python",
          topic: "if-else",
          topicLabel: "If-Else",
          conceptTo: "/11th/computer/python/programs/",
          chapterTo: "/11th/computer/python/",
        },
      ),
      withMeta(researchedPythonMcqAdditionsByGroup["if-else"] ?? [], {
        unit: "python",
        topic: "if-else",
        topicLabel: "If-Else",
        conceptTo: "/11th/computer/python/programs/",
        chapterTo: "/11th/computer/python/",
      }),
    ),
  },
  {
    key: "loops",
    label: "Loops",
    description: "For aur while loops ke core MCQs.",
    conceptTo: "/11th/computer/python/loops/",
    questions: mergeUniqueQuestions(
      withMeta(loopsExercises.mcqs, {
        unit: "python",
        topic: "loops",
        topicLabel: "Loops",
        conceptTo: "/11th/computer/python/loops/",
        chapterTo: "/11th/computer/python/",
      }),
      withMeta(researchedPythonMcqAdditionsByGroup.loops ?? [], {
        unit: "python",
        topic: "loops",
        topicLabel: "Loops",
        conceptTo: "/11th/computer/python/loops/",
        chapterTo: "/11th/computer/python/",
      }),
    ),
  },
  {
    key: "functions",
    label: "Functions",
    description: "Functions aur parameters ke MCQs.",
    conceptTo: "/11th/computer/python/functions/",
    questions: mergeUniqueQuestions(
      withMeta(
        [
          {
            id: "fn1",
            difficulty: "easy",
            question: "Function ka faida kya hai?",
            options: ["Code repeat aur organize karna", "Sirf input lena", "Sirf output dikhana", "List remove karna"],
            correctIndex: 0,
            explanation: "Function reusable code block deta hai jis se code organize aur short hota hai.",
          },
          {
            id: "fn2",
            difficulty: "easy",
            question: "Function me parameter kya hota hai?",
            options: ["Function ka naam", "Function ke andar aane wali value", "Sirf output", "Comment line"],
            correctIndex: 1,
            explanation: "Parameter wo input hota hai jo function ke andar use hota hai.",
          },
        ],
        {
          unit: "python",
          topic: "functions",
          topicLabel: "Functions",
          conceptTo: "/11th/computer/python/functions/",
          chapterTo: "/11th/computer/python/",
        },
      ),
      withMeta(researchedPythonMcqAdditionsByGroup.functions ?? [], {
        unit: "python",
        topic: "functions",
        topicLabel: "Functions",
        conceptTo: "/11th/computer/python/functions/",
        chapterTo: "/11th/computer/python/",
      }),
    ),
  },
  {
    key: "lists",
    label: "Lists",
    description: "Lists ko Chapter 2 to Chapter 4 bridge ke taur par samjho.",
    conceptTo: "/11th/computer/data-structures/",
    questions: mergeUniqueQuestions(
      withMeta(
        [
          {
            id: "list1",
            difficulty: "easy",
            question: "List me last me naya item add karne ke liye kya use hota hai?",
            options: ["append()", "remove()", "pop()", "print()"],
            correctIndex: 0,
            explanation: "append() list ke end me naya item add karta hai.",
          },
          {
            id: "list2",
            difficulty: "easy",
            question: "List ke specific index ka item kaise access hota hai?",
            options: ["list(index)", "list[index]", "list{index}", "index(list)"],
            correctIndex: 1,
            explanation: "Square brackets ke andar index likh kar item access hota hai.",
          },
        ],
        {
          unit: "python",
          topic: "lists",
          topicLabel: "Lists",
          conceptTo: "/11th/computer/data-structures/",
          chapterTo: "/11th/computer/python/",
        },
      ),
      withMeta(researchedPythonMcqAdditionsByGroup.lists ?? [], {
        unit: "python",
        topic: "lists",
        topicLabel: "Lists",
        conceptTo: "/11th/computer/data-structures/",
        chapterTo: "/11th/computer/python/",
      }),
    ),
  },
];

const pythonQuestions = pythonTopicGroups.flatMap((group) => group.questions);

function buildChapter(seed: ChapterSeed): ComputerMcqChapter {
  const questions = seed.questions ?? seed.groups?.flatMap((group) => group.questions) ?? [];
  const groups = seed.groups ?? [
    {
      key: "all",
      label: `${seed.shortTitle} MCQs`,
      description: seed.description,
      questions,
    },
  ];

  return {
    ...seed,
    questions,
    groups,
  };
}

function buildStandardChapter(options: {
  chapter: number;
  slug: string;
  unit: ComputerMcqUnit;
  shortTitle: string;
  title: string;
  description: string;
  priority: ComputerMcqChapter["priority"];
  seo: ComputerMcqChapter["seo"];
  routes: ComputerMcqChapter["routes"];
  questions: ComputerMcq[];
}): ComputerMcqChapter {
  return buildChapter({
    ...options,
    questions: mergeUniqueQuestions(
      withMeta(options.questions, {
        unit: options.unit,
        topic: options.unit,
        topicLabel: options.shortTitle,
        conceptTo: options.routes.chapterTo,
        chapterTo: options.routes.chapterTo,
      }),
      withMeta(researchedMcqAdditionsByUnit[options.unit] ?? [], {
        unit: options.unit,
        topic: options.unit,
        topicLabel: options.shortTitle,
        conceptTo: options.routes.chapterTo,
        chapterTo: options.routes.chapterTo,
      }),
    ),
  });
}

export const computerMcqChapters: ComputerMcqChapter[] = [
  buildStandardChapter({
    chapter: 1,
    slug: "chapter-1-sdlc",
    unit: "sdlc",
    shortTitle: "SDLC",
    title: "Chapter 1 - SDLC",
    description: "Software Development Life Cycle ke objective questions aur fast revision.",
    priority: "high",
    seo: {
      title: "11th Class SDLC MCQs | Ustaad",
      description: "Chapter 1 SDLC MCQs, solved objective questions aur quick revision flow.",
      keywords: "11th class sdlc mcqs, chapter 1 computer mcqs, first year sdlc mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-1-sdlc/",
      chapterTo: "/11th/computer/sdlc/",
      mcqTo: "/11th/computer/sdlc/mcqs/",
      notesTo: "/11th/computer/sdlc/",
      exerciseTo: "/11th/computer/sdlc/exercise/",
    },
    questions: verifiedTopicExercises.sdlc?.practiceMcqs ?? sdlcExercises.mcqs,
  }),
  buildChapter({
    chapter: 2,
    slug: "chapter-2-python",
    unit: "python",
    shortTitle: "Python",
    title: "Chapter 2 - Python",
    description: "Variables, operators, loops aur chapter-style Python MCQs.",
    priority: "critical",
    seo: {
      title: "11th Class Python MCQs - Chapter 2 Solved Questions",
      description: "Practice chapter 2 Python MCQs with topic-wise questions, answers and explanations.",
      keywords: "11th class python mcqs, chapter 2 python mcqs, first year computer python mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-2-python/",
      chapterTo: "/11th/computer/python/",
      mcqTo: "/11th/computer/python/mcqs/",
      notesTo: "/11th/computer/python/notes/",
      exerciseTo: "/11th/computer/python/exercise/",
    },
    groups: pythonTopicGroups,
  }),
  buildStandardChapter({
    chapter: 3,
    slug: "chapter-3-algorithms",
    unit: "algorithm",
    shortTitle: "Algorithms",
    title: "Chapter 3 - Algorithms",
    description: "Algorithm, flowchart, sorting aur searching ke MCQs.",
    priority: "high",
    seo: {
      title: "11th Class Algorithms MCQs | Ustaad",
      description: "Chapter 3 algorithm MCQs with objective questions for flowchart, sorting and searching.",
      keywords: "11th class algorithm mcqs, chapter 3 computer mcqs, first year algorithm mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-3-algorithms/",
      chapterTo: "/11th/computer/algorithm/",
      mcqTo: "/11th/computer/algorithm/mcqs/",
      notesTo: "/11th/computer/algorithm/",
      exerciseTo: "/11th/computer/algorithm/exercise/",
    },
    questions: verifiedTopicExercises.algorithm?.practiceMcqs ?? algorithmExercises.mcqs,
  }),
  buildStandardChapter({
    chapter: 4,
    slug: "chapter-4-data-structures",
    unit: "data-structures",
    shortTitle: "Data Structures",
    title: "Chapter 4 - Data Structures",
    description: "Lists, stacks, queues aur trees ke objective questions.",
    priority: "high",
    seo: {
      title: "11th Class Data Structures MCQs | Ustaad",
      description: "Chapter 4 data structures MCQs with lists, stacks, queues and trees objective questions.",
      keywords: "11th class data structures mcqs, chapter 4 computer mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-4-data-structures/",
      chapterTo: "/11th/computer/data-structures/",
      mcqTo: "/11th/computer/data-structures/mcqs/",
      notesTo: "/11th/computer/data-structures/",
      exerciseTo: "/11th/computer/data-structures/exercise/",
    },
    questions: verifiedTopicExercises["data-structures"]?.practiceMcqs ?? dsExercises.mcqs,
  }),
  buildStandardChapter({
    chapter: 5,
    slug: "chapter-5-data-analytics",
    unit: "data-analytics",
    shortTitle: "Data Analytics",
    title: "Chapter 5 - Data Analytics",
    description: "Visualization, models aur experiments ke MCQs.",
    priority: "medium",
    seo: {
      title: "11th Class Data Analytics MCQs | Ustaad",
      description: "Chapter 5 data analytics MCQs for visualization, models and experiments.",
      keywords: "11th class data analytics mcqs, chapter 5 computer mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-5-data-analytics/",
      chapterTo: "/11th/computer/data-analytics/",
      mcqTo: "/11th/computer/data-analytics/mcqs/",
      notesTo: "/11th/computer/data-analytics/",
      exerciseTo: "/11th/computer/data-analytics/exercise/",
    },
    questions: verifiedTopicExercises["data-analytics"]?.practiceMcqs ?? dataAnalyticsExercises.mcqs,
  }),
  buildStandardChapter({
    chapter: 6,
    slug: "chapter-6-emerging-technologies",
    unit: "emerging-tech",
    shortTitle: "Emerging Technologies",
    title: "Chapter 6 - Emerging Technologies",
    description: "Cloud, blockchain aur serverless ke MCQs.",
    priority: "medium",
    seo: {
      title: "11th Class Emerging Technologies MCQs | Ustaad",
      description: "Chapter 6 emerging technologies MCQs for cloud, blockchain and serverless concepts.",
      keywords: "11th class emerging technologies mcqs, chapter 6 computer mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-6-emerging-technologies/",
      chapterTo: "/11th/computer/emerging-tech/",
      mcqTo: "/11th/computer/emerging-tech/mcqs/",
      notesTo: "/11th/computer/emerging-tech/",
      exerciseTo: "/11th/computer/emerging-tech/exercise/",
    },
    questions: verifiedTopicExercises["emerging-tech"]?.practiceMcqs ?? emergingTechExercises.mcqs,
  }),
  buildStandardChapter({
    chapter: 7,
    slug: "chapter-7-legal-and-ethical-aspects",
    unit: "legal-ethics",
    shortTitle: "Legal & Ethical Aspects",
    title: "Chapter 7 - Legal and Ethical Aspects",
    description: "Privacy, threats aur responsible behavior ke MCQs.",
    priority: "low",
    seo: {
      title: "11th Class Legal and Ethical Aspects MCQs | Ustaad",
      description: "Chapter 7 legal and ethical aspects MCQs for privacy, threats and digital behavior.",
      keywords: "11th class legal and ethical aspects mcqs, chapter 7 computer mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-7-legal-and-ethical-aspects/",
      chapterTo: "/11th/computer/legal-ethics/",
      mcqTo: "/11th/computer/legal-ethics/mcqs/",
      notesTo: "/11th/computer/legal-ethics/",
      exerciseTo: "/11th/computer/legal-ethics/exercise/",
    },
    questions: verifiedTopicExercises["legal-ethics"]?.practiceMcqs ?? legalEthicsExercises.mcqs,
  }),
  buildStandardChapter({
    chapter: 8,
    slug: "chapter-8-digital-literacy",
    unit: "digital-literacy",
    shortTitle: "Digital Literacy",
    title: "Chapter 8 - Digital Literacy",
    description: "Search, evaluation aur academic resources ke MCQs.",
    priority: "low",
    seo: {
      title: "11th Class Digital Literacy MCQs | Ustaad",
      description: "Chapter 8 digital literacy MCQs for search, evaluation and online resources.",
      keywords: "11th class digital literacy mcqs, chapter 8 computer mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-8-digital-literacy/",
      chapterTo: "/11th/computer/digital-literacy/",
      mcqTo: "/11th/computer/digital-literacy/mcqs/",
      notesTo: "/11th/computer/digital-literacy/",
      exerciseTo: "/11th/computer/digital-literacy/exercise/",
    },
    questions: verifiedTopicExercises["digital-literacy"]?.practiceMcqs ?? digitalLiteracyExercises.mcqs,
  }),
  buildStandardChapter({
    chapter: 9,
    slug: "chapter-9-entrepreneurship",
    unit: "entrepreneurship",
    shortTitle: "Entrepreneurship",
    title: "Chapter 9 - Entrepreneurship",
    description: "Design thinking, business plan aur finance ke MCQs.",
    priority: "low",
    seo: {
      title: "11th Class Entrepreneurship MCQs | Ustaad",
      description: "Chapter 9 entrepreneurship MCQs for design thinking, business plan and finance.",
      keywords: "11th class entrepreneurship mcqs, chapter 9 computer mcqs",
    },
    routes: {
      clusterTo: "/11th/computer/mcqs/chapter-9-entrepreneurship/",
      chapterTo: "/11th/computer/entrepreneurship/",
      mcqTo: "/11th/computer/entrepreneurship/mcqs/",
      notesTo: "/11th/computer/entrepreneurship/",
      exerciseTo: "/11th/computer/entrepreneurship/exercise/",
    },
    questions: verifiedTopicExercises.entrepreneurship?.practiceMcqs ?? entrepreneurshipExercises.mcqs,
  }),
];

export const computerMcqChapterBySlug = Object.fromEntries(
  computerMcqChapters.map((chapter) => [chapter.slug, chapter]),
) as Record<string, ComputerMcqChapter>;

export const computerMcqChapterByUnit = Object.fromEntries(
  computerMcqChapters.map((chapter) => [chapter.unit, chapter]),
) as Record<ComputerMcqUnit, ComputerMcqChapter>;

const top100Targets: Record<ComputerMcqUnit, number> = {
  python: 24,
  sdlc: 14,
  algorithm: 14,
  "data-structures": 12,
  "data-analytics": 9,
  "emerging-tech": 9,
  "legal-ethics": 6,
  "digital-literacy": 6,
  entrepreneurship: 6,
};

export const computerTop100Mcqs = computerMcqChapters.flatMap((chapter) =>
  chapter.questions.slice(0, top100Targets[chapter.unit]),
).slice(0, 100);

export const pythonMcqTopicKeys = pythonTopicGroups.map((group) => group.key);
export const pythonMcqTopicGroupByKey = Object.fromEntries(
  pythonTopicGroups.map((group) => [group.key, group]),
) as Record<string, (typeof pythonTopicGroups)[number]>;
export const pythonMcqQuestions = pythonQuestions;
